import { ChangeDetectorRef, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material';
import { MatStepper } from '@angular/material/stepper';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoService } from '@ngneat/transloco';
import { ApolloError } from 'apollo-client/errors/ApolloError';
import { of } from 'rxjs';
import { CreateIntakeFormInput, IntakeFormStatus, QuestionnaireType } from 'src/API';
import { PatientService } from '../core/api/patient.service';
import { QuestionnaireRequestService } from '../core/api/questionnaire-request.service';
import { standard_intakeForm } from '../form-template/form-field-template-library';
import { FormTemplateInputFormGroup } from '../form-template/form-template-input-form-group';
import { FormTemplateSchema } from '../form-template/form-template-model';
import { allQuestionnairesScoringConfig } from '../questionnaires/custom-questionnaire/custom-questionnaire-scoring/custom-questionnaire-scoring-schema';
import { OSDIService } from '../questionnaires/osdi/osdi.service';
import { QuestionnaireRoutePath } from '../questionnaires/questionnaires-routing.module';
import { ConsentFormComponent } from '../questionnaires/simple-questionnaire/consent-form/consent-form.component';
import { SPEEDService } from '../questionnaires/speed/speed.service';
import { BaseAttachmentsService } from '../shared/attachments/base-attachments.service';
import { S3AttachmentService } from '../shared/attachments/s3-attachment.service';
import { AutopopulateService } from '../shared/autopopulate/autopopulate.service';
import { FormErrorScrollingService } from '../shared/form-error-scrolling/form-error-scrolling.service';
import {
  allIntakeFormSchemas,
  generalButtonMap,
  IntakeFormGroupType,
  IntakeFormSchema,
  IntakeFormType
} from './intake-form-schema';
import { IntakeFormService } from './intake-form.service';

@Component({
  selector: 'csi-intake-form',
  templateUrl: './intake-form.component.html',
  styleUrls: ['./intake-form.component.css'],
  providers: [SPEEDService, OSDIService]
})
export class IntakeFormComponent implements OnInit {
  @ViewChild('stepper', { static: false }) private stepper: MatStepper;

  public formTemplateInputFormGroup: FormTemplateInputFormGroup;
  public schema = standard_intakeForm;
  private allQuestionnairesScoringConfig = allQuestionnairesScoringConfig;

  public submitFormControl = new FormControl();
  private clinicId: string;
  public country: string;

  public intakeFormSchema: IntakeFormSchema;
  public intakeFormGroup = new FormGroup({});
  public IntakeFormGroupType = IntakeFormGroupType;
  public QuestionnaireType = QuestionnaireType;

  public groupChanges;
  public buttonMaps = generalButtonMap;
  private activatedRoute: ActivatedRoute;
  public intakeFormType: string;
  public loading = true;
  public submitting = false;

  constructor(
    private intakeFormService: IntakeFormService,
    private route: ActivatedRoute,
    private dialog: MatDialog,
    public speedService: SPEEDService,
    public osdiService: OSDIService,
    private router: Router,
    private patientService: PatientService,
    public translocoService: TranslocoService,
    private hostElementRef: ElementRef,
    private formErrorScrollingService: FormErrorScrollingService,
    private changeDetectorRef: ChangeDetectorRef,
    private autopopulateService: AutopopulateService,
    private s3AttachmentService: S3AttachmentService,
    private baseAttachmentsService: BaseAttachmentsService,
    private questionnaireRequestService: QuestionnaireRequestService
  ) {}

  ngOnInit() {
    this.clinicId = this.route.snapshot.params.id;
    this.country = this.route.snapshot.queryParams.country || 'Canada';
    this.intakeFormType = this.route.snapshot.queryParams.type;

    if (this.intakeFormService.defaultIntakeFormTemplateSchemas[this.intakeFormType]) {
      this.loading = false;
      this.schema = this.intakeFormService.defaultIntakeFormTemplateSchemas[this.intakeFormType];
      this.formTemplateInputFormGroup = new FormTemplateInputFormGroup(
        this.schema as FormTemplateSchema
      );
      this.formTemplateInputFormGroup.addControl(
        'consentsToPrivacyForm',
        new FormControl(null, Validators.requiredTrue)
      );
    } else {
      this.intakeFormService.getFormTemplate(this.intakeFormType).subscribe(data => {
        this.schema = JSON.parse(data.schema);
        this.loading = false;
        this.formTemplateInputFormGroup = new FormTemplateInputFormGroup(
          this.schema as FormTemplateSchema
        );
        this.formTemplateInputFormGroup.addControl(
          'consentsToPrivacyForm',
          new FormControl(null, Validators.requiredTrue)
        );
      });
    }

    this.intakeFormSchema =
      allIntakeFormSchemas[this.intakeFormType] || allIntakeFormSchemas[IntakeFormType.STANDARD];

    Object.keys(this.intakeFormSchema.groups).forEach(key => {
      const group = this.intakeFormSchema.groups[key];
      this.intakeFormGroup.addControl(key, this.generateFormGroup(group.config));
    });
  }
  public changeLanguage(lang = 'en') {
    this.translocoService.load(lang).subscribe(() => {
      this.translocoService.setActiveLang(lang);
    });
  }

  generateFormGroup(questionList: any) {
    const formGroup = new FormGroup({});
    Object.keys(questionList).forEach(question => {
      const validators = questionList[question].validators || null;
      const questions = questionList[question];

      if (questions.inputType.type === 'checkbox') {
        formGroup.addControl(question, new FormControl(false, validators));
      } else if (questions.inputType.type === 'checkboxGroup') {
        formGroup.addControl(
          question,
          new FormGroup(
            Object.keys(questions.inputType.checkboxes).reduce(
              (group, key) => ({ ...group, [key]: new FormControl() }),
              {}
            ),
            validators
          )
        );
      } else if (questions.inputType.type === 'slider') {
        formGroup.addControl(question, new FormControl(questions.inputType.step, validators));
      } else {
        formGroup.addControl(question, new FormControl(null, validators));
      }
    });
    formGroup.valueChanges.subscribe(() => {
      setTimeout(() => {
        this.groupChanges = new Date();
        setTimeout(() => {
          setTimeout(() => {
            setTimeout(() => {
              this.changeDetectorRef.markForCheck();
            });
          });
        });
      });
    });
    return formGroup;
  }

  noSort() {
    return 0;
  }

  onSubmit() {
    if (this.submitting) {
      return;
    }
    this.formTemplateInputFormGroup.markAllAsTouched();
    if (this.formTemplateInputFormGroup.valid) {
      this.submitting = true;
      this.formTemplateInputFormGroup.controls['consentsToPrivacyForm'].disable();

      // Upload files to s3 and replace current attachments with s3Objects that contain bucket info
      (this.formTemplateInputFormGroup.attachments.value.length > 0
        ? this.s3AttachmentService.uploadFiles(
            this.formTemplateInputFormGroup.attachments.value,
            this.clinicId
          )
        : of([])
      ).subscribe(s3ObjectAttachments => {
        const formControlAttachments = this.formTemplateInputFormGroup.attachments.value;
        const updatedAttachments = s3ObjectAttachments.reduce((acc, s3Attachment, i) => {
          acc = [
            ...acc,
            {
              ...s3Attachment,
              metadata: JSON.stringify({
                ...(s3Attachment['metadata'] || {}), // Merge with S3 metadata
                ...JSON.parse(formControlAttachments[i].metadata)
              })
            }
          ];
          return acc;
        }, []);
        this.formTemplateInputFormGroup.attachments.patchValue(updatedAttachments);

        const response = {
          answers: this.formTemplateInputFormGroup.value,
          scores: this.getScores()
        };
        const input: CreateIntakeFormInput = {
          status: IntakeFormStatus.none,
          response: JSON.stringify(response),
          config: JSON.stringify({
            schema: this.schema
          }),
          intakeFormClinicId: this.clinicId
        };
        this.formTemplateInputFormGroup.disable();
        this.intakeFormService.createIntakeFromInput(input).subscribe(
          () => {
            this.submitting = false;
            this.redirectToSuccessPage();
          },
          (error: ApolloError) => {
            console.log(error);
            window.alert(
              this.translocoService.translate(
                'alerts.We have encountered an error, please contact support@csidryeye.com.'
              )
            );
          }
        );
      });
    }
  }

  getScores() {
    const scores = this.allQuestionnairesScoringConfig.reduce((acc, questionnaireScoringConfig) => {
      // scoring calculator will return NaN if there are any missing keys
      const score = questionnaireScoringConfig.scoringCalculator(
        this.autopopulateService.flattenByOneLevel(this.formTemplateInputFormGroup.value),
        questionnaireScoringConfig.questionnaireKeys
      );
      // scores should not be negative
      if (score >= 0) {
        acc = {
          ...acc,
          [questionnaireScoringConfig.questionnaireType]: score
        };
      }
      return acc;
    }, {});

    return scores;
  }

  openPrivacyConsentForm(event) {
    event.preventDefault();
    this.dialog.open(ConsentFormComponent, {
      data: { country: this.country }
    });
  }

  private redirectToSuccessPage() {
    // let isPositive = false;
    // let hideVideo = true;
    // if (osdiScore && speedScore) {
    //   hideVideo = false;
    //   const scores = JSON.stringify({
    //     [QuestionnaireType.OSDI]: osdiScore,
    //     [QuestionnaireType.SPEED]: speedScore
    //   });

    //   isPositive = this.questionnaireRequestService.isAtRisk(scores);
    // }

    this.router.navigate([`questionnaires/${QuestionnaireRoutePath.ThankYou}`], {
      relativeTo: this.activatedRoute,
      queryParams: {
        isPositive: false,
        hideVideo: true,
        messageToShow: 'thankYouForSubmittingIntake',
        lang: this.translocoService.getActiveLang()
      }
    });
  }

  next(formGroup: FormGroup) {
    setTimeout(() => {
      formGroup.markAllAsTouched();
      formGroup.updateValueAndValidity();
      if (formGroup.valid) {
        this.stepper.next();
      } else {
        this.formErrorScrollingService.scrollToFirstInvalidControlInView(
          this.hostElementRef,
          true,
          {
            isHostScrollable: true,
            isHostMatStep: true
          }
        );
      }
    });
  }
}
